/**
 * markdownToHtml.ts
 *
 * A lightweight, zero-dependency Markdown → sanitized HTML converter
 * suitable for rendering newsletter body_md content in the browser.
 *
 * Security note: all rendered HTML goes through sanitizeHtml() before use.
 * isSafeUrl() is the single source of truth for link/src safety.
 */

// ─── URL Safety ───────────────────────────────────────────────────────────────

/**
 * Returns true when the URL is safe to embed in an href or src attribute.
 *
 * Order of checks (IMPORTANT — do not reorder):
 *   1. Reject protocol-relative URLs  (//)  — these bypass scheme checks
 *   2. Allow mailto: and https?:  absolute URLs
 *   3. Allow true relative paths (start with /)
 *   4. Reject anything else (javascript:, data:, vbscript:, etc.)
 */
export function isSafeUrl(url: string): boolean {
  const trimmed = url.trim()

  // 1. Reject protocol-relative URLs first — MUST come before the '/' check
  if (trimmed.startsWith('//')) return false

  // 2. Safe absolute schemes
  if (
    trimmed.startsWith('https://') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('mailto:')
  ) {
    return true
  }

  // 3. True relative paths (SPA-internal or same-origin asset)
  if (trimmed.startsWith('/')) return true

  // 4. Reject everything else
  return false
}

/**
 * Returns true when the URL points to an external resource that should open
 * in a new tab (rel="noopener noreferrer").
 * SPA-internal paths (starting with '/') must be rendered as <Link>, not <a target="_blank">.
 * mailto: links are also treated as external so they open the mail client rather than
 * being intercepted by the SPA router.
 */
export function isExternalUrl(url: string): boolean {
  const trimmed = url.trim()
  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('mailto:')
  )
}

// ─── HTML escape ──────────────────────────────────────────────────────────────

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// ─── Inline Markdown → HTML ───────────────────────────────────────────────────

/**
 * Convert inline Markdown (bold, italic, code, links, images) to HTML.
 *
 * Strategy — pre-escape then replace:
 *   1. HTML-escape the ENTIRE input first so that any literal `<`, `>`, `&`,
 *      `"`, `'` in the source text can never become live HTML.
 *   2. Run Markdown replacers. The Markdown control characters (`*`, `_`, `~`,
 *      `` ` ``, `[`, `]`, `(`, `)`) are NOT touched by escapeHtml, so all
 *      Markdown patterns still match correctly.
 *   3. The replacers emit safe HTML tags and must NOT re-escape already-escaped
 *      content (e.g. code content is already escaped from step 1; links/labels
 *      are already escaped — so replacers just wrap them in tags).
 *
 * This eliminates the double-encoding hazard that occurred when escapeTextNodes
 * was called as a final pass after some replacers had already escaped content.
 *
 * Callers must NOT pre-escape the input.
 */
function parseInline(text: string): string {
  // Step 1 — HTML-escape all plain text up-front. Markdown control characters
  // are unaffected so patterns below still fire.
  text = escapeHtml(text)

  // Step 2 — Markdown replacers. All captured content is already HTML-escaped;
  // replacers only need to wrap it in safe HTML elements.

  // Inline code — content is already escaped; just wrap it.
  text = text.replace(/`([^`]+)`/g, (_, code) => `<code>${code}</code>`)

  // Bold + italic  ***text***
  text = text.replace(/\*\*\*(.+?)\*\*\*/g, (_, inner: string) => `<strong><em>${inner}</em></strong>`)

  // Bold  **text**
  text = text.replace(/\*\*(.+?)\*\*/g, (_, inner: string) => `<strong>${inner}</strong>`)

  // Italic  *text*  or  _text_
  text = text.replace(/\*([^*]+)\*/g, (_, inner: string) => `<em>${inner}</em>`)
  text = text.replace(/_([^_]+)_/g, (_, inner: string) => `<em>${inner}</em>`)

  // Strikethrough  ~~text~~
  text = text.replace(/~~(.+?)~~/g, (_, inner: string) => `<del>${inner}</del>`)

  // Markdown links  [label](url)
  // Both label and url are already HTML-escaped from step 1. We need the RAW
  // url to pass to isSafeUrl/isExternalUrl — unescape it first, then re-escape
  // for the href attribute (it is already escaped, so this is a no-op for
  // normal URLs; it re-escapes any edge cases safely).
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label: string, escapedUrl: string) => {
    // Unescape the URL to check scheme (isSafeUrl works on raw URLs)
    const rawUrl = escapedUrl
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
    if (!isSafeUrl(rawUrl)) return label   // label is already escaped
    // Re-escape the url for the attribute (already escaped, but re-escape is
    // idempotent for simple URLs and safe for edge cases)
    const safeUrl = escapeHtml(rawUrl)
    if (isExternalUrl(rawUrl)) {
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`
    }
    return `<a href="${safeUrl}" data-spa-link="true">${label}</a>`
  })

  // Bare URLs  https?://...
  // The URL was already HTML-escaped (& → &amp; etc). Use it directly in href.
  text = text.replace(/(https?:\/\/[^\s<>"&]+)/g, (safeUrl) => {
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeUrl}</a>`
  })

  return text
}

// ─── Block-level parser ───────────────────────────────────────────────────────

type Block =
  | { type: 'heading'; level: number; content: string }
  | { type: 'hr' }
  | { type: 'blockquote'; lines: string[] }
  | { type: 'code'; lang: string; lines: string[] }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'paragraph'; lines: string[] }

function parseBlocks(lines: string[]): Block[] {
  const blocks: Block[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // Fenced code block
    const fenceMatch = line.match(/^```(\w*)$/)
    if (fenceMatch) {
      const lang = fenceMatch[1] ?? ''
      const codeLines: string[] = []
      i++
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }
      i++ // skip closing ```
      blocks.push({ type: 'code', lang, lines: codeLines })
      continue
    }

    // Heading
    const hMatch = line.match(/^(#{1,6})\s+(.+)$/)
    if (hMatch) {
      blocks.push({ type: 'heading', level: hMatch[1].length, content: hMatch[2] })
      i++
      continue
    }

    // Horizontal rule
    if (/^(-{3,}|_{3,}|\*{3,})$/.test(line.trim())) {
      blocks.push({ type: 'hr' })
      i++
      continue
    }

    // Blockquote
    if (line.startsWith('> ')) {
      const bqLines: string[] = []
      while (i < lines.length && lines[i].startsWith('> ')) {
        bqLines.push(lines[i].slice(2))
        i++
      }
      blocks.push({ type: 'blockquote', lines: bqLines })
      continue
    }

    // Unordered list
    if (/^[-*+]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*+]\s+/, ''))
        i++
      }
      blocks.push({ type: 'ul', items })
      continue
    }

    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''))
        i++
      }
      blocks.push({ type: 'ol', items })
      continue
    }

    // Blank line — skip
    if (line.trim() === '') {
      i++
      continue
    }

    // Paragraph — collect until blank line or block boundary
    const paraLines: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('#') &&
      !lines[i].startsWith('> ') &&
      !/^[-*+]\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !/^(---+|___+|\*\*\*+)$/.test(lines[i].trim()) &&
      !lines[i].startsWith('```')
    ) {
      paraLines.push(lines[i])
      i++
    }
    if (paraLines.length > 0) {
      blocks.push({ type: 'paragraph', lines: paraLines })
    }
  }

  return blocks
}

// ─── Block → HTML ─────────────────────────────────────────────────────────────

function renderBlock(block: Block): string {
  switch (block.type) {
    case 'heading': {
      const tag = `h${block.level}`
      // Pass raw content to parseInline — it handles escaping internally.
      return `<${tag}>${parseInline(block.content)}</${tag}>`
    }
    case 'hr':
      return '<hr />'
    case 'blockquote': {
      // Pass raw lines to parseInline — it handles escaping internally.
      const inner = block.lines.map((l) => `<p>${parseInline(l)}</p>`).join('')
      return `<blockquote>${inner}</blockquote>`
    }
    case 'code': {
      // Fenced code blocks bypass parseInline entirely — escape here directly.
      const body = block.lines.map(escapeHtml).join('\n')
      const cls = block.lang ? ` class="language-${escapeHtml(block.lang)}"` : ''
      return `<pre><code${cls}>${body}</code></pre>`
    }
    case 'ul': {
      // Pass raw items to parseInline — it handles escaping internally.
      const items = block.items.map((it) => `<li>${parseInline(it)}</li>`).join('')
      return `<ul>${items}</ul>`
    }
    case 'ol': {
      // Pass raw items to parseInline — it handles escaping internally.
      const items = block.items.map((it) => `<li>${parseInline(it)}</li>`).join('')
      return `<ol>${items}</ol>`
    }
    case 'paragraph': {
      // Pass raw lines to parseInline — it handles escaping internally.
      const html = block.lines.map((l) => parseInline(l)).join('<br />')
      return `<p>${html}</p>`
    }
  }
}

// ─── Sanitize ─────────────────────────────────────────────────────────────────

/**
 * Strip dangerous HTML patterns from the rendered output.
 *
 * Because all user-supplied text has already been HTML-escaped by escapeHtml()
 * before it reaches parseInline/renderBlock, this function is a belt-and-suspenders
 * guard: it removes any script/style/iframe elements and event-handler attributes
 * that could have been injected through Markdown constructs (e.g. bare HTML in the
 * source). The final output is safe to assign to dangerouslySetInnerHTML.
 */
export function sanitizeHtml(html: string): string {
  // Remove script/style/iframe/etc. entirely (belt-and-suspenders)
  html = html.replace(/<(script|style|iframe|object|embed|form)[^>]*>[\s\S]*?<\/\1>/gi, '')
  html = html.replace(/<(script|style|iframe|object|embed|form)[^>]*/gi, '')

  // Strip event attributes  on*=...
  // The pattern uses \s* (zero or more whitespace) so it also catches handlers
  // that immediately follow a slash or another attribute with no space, e.g.
  //   <img/onerror=alert(1)>          — slash + no space
  //   <img src="x"onerror="...">      — attribute end-quote + no space
  html = html.replace(/\s*\bon\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '')

  // Strip javascript: in href/src (extra safety)
  html = html.replace(
    /\s+(href|src|action)\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi,
    '',
  )

  return html
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Convert a Markdown string to sanitized HTML.
 *
 * Safe to assign to dangerouslySetInnerHTML — all user-supplied text is
 * HTML-escaped before parsing, and the output passes through sanitizeHtml().
 */
export function markdownToHtml(md: string): string {
  const lines = md.replace(/\r\n/g, '\n').split('\n')
  const blocks = parseBlocks(lines)
  const raw = blocks.map(renderBlock).join('\n')
  return sanitizeHtml(raw)
}
